import { getCommonBrandIds, getLocationBrandIds } from './location.brands'
import {
  CarHireLocation,
  CarHireLocationVendor,
  LocationOpeningTimes,
} from './schemas/location.dto'

const TIME_RANGE_SEPARATOR = /\s*[—–-]\s*/
const WEEK_DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

const toMinutes = (time: string): number | null => {
  const match = time.trim().match(/^(\d{1,2}):(\d{1,2})$/)
  if (!match) {
    return null
  }
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 24 || minutes > 59) {
    return null
  }
  return hours * 60 + minutes
}

const parseRange = (entry: string): [number, number] | null => {
  const [start, end, ...extra] = entry.split(TIME_RANGE_SEPARATOR)
  if (extra.length > 0 || !start || !end) {
    return null
  }
  const startMinutes = toMinutes(start)
  const endMinutes = toMinutes(end)
  return startMinutes === null || endMinutes === null ? null : [startMinutes, endMinutes]
}

const hoursForDay = (
  openingTimes: LocationOpeningTimes[],
  dayIndex: number
): string[] | undefined => {
  const dayName = WEEK_DAYS[((dayIndex % 7) + 7) % 7]
  return openingTimes.find((entry) => entry.day.trim().toLowerCase() === dayName)?.hours
}

const findVendor = (
  location: CarHireLocation | undefined | null,
  brandId: string
): CarHireLocationVendor | undefined =>
  location?.vendors?.find((vendor) => vendor.brandId === brandId)

const hasOvernightOpening = (
  openingTimes: LocationOpeningTimes[],
  dayIndex: number,
  minuteOfDay?: number
): boolean =>
  (hoursForDay(openingTimes, dayIndex - 1) ?? []).some((entry) => {
    const range = parseRange(entry)
    if (!range) {
      return false
    }
    const [start, end] = range
    return end < start && (minuteOfDay === undefined || minuteOfDay <= end)
  })

/**
 * Whether a brand's own desk is open at a moment in time.
 *
 * Unknown is treated as open. Our stored hours are synced once a day and can drift, so the only
 * thing worth acting on is positive evidence that a desk is shut - a wrong "shut" would hide cars
 * that are really on sale, which is the failure this whole design exists to remove.
 */
export const isBrandOpenAt = (
  location: CarHireLocation | undefined | null,
  brandId: string,
  dateTime: Date
): boolean => {
  const openingTimes = findVendor(location, brandId)?.openingTimes
  if (!openingTimes?.length) {
    return true
  }

  const dayIndex = dateTime.getDay()
  const minuteOfDay = dateTime.getHours() * 60 + dateTime.getMinutes()

  const todaysHours = hoursForDay(openingTimes, dayIndex)
  // A period that started yesterday and runs past midnight still covers this morning.
  const openFromYesterday = hasOvernightOpening(openingTimes, dayIndex, minuteOfDay)

  // A missing day or a day whose every period is unparseable is unknown, not shut. An explicit
  // empty day is positive evidence that the desk is closed (formatOpeningTimes converts the
  // upstream 00:00-00:00 closed-day sentinel into that shape).
  if (todaysHours === undefined) {
    return true
  }
  const todaysRanges = (todaysHours ?? [])
    .map(parseRange)
    .filter((range): range is [number, number] => range !== null)
  if (!todaysRanges.length) {
    return todaysHours.length ? true : openFromYesterday
  }

  const openToday = todaysRanges.some(([start, end]) => {
    if (end === start) {
      // Equal start/end values are zero-length periods, including the 00:00-00:00 closed marker.
      return false
    }
    // A period whose end is before its start runs past midnight (Charlotte publishes
    // 05:00-02:00), so today it covers everything from the start time onwards.
    return end > start ? minuteOfDay >= start && minuteOfDay <= end : minuteOfDay >= start
  })

  return openToday || openFromYesterday
}

/** Whether published hours positively identify a location as closed for an entire date. */
export const isClosedOnDate = (
  openingTimes: LocationOpeningTimes[] | undefined,
  date: Date
): boolean => {
  if (!openingTimes?.length) {
    return false
  }

  const todaysHours = hoursForDay(openingTimes, date.getDay())
  if (todaysHours === undefined) {
    return false
  }

  const todaysRanges = todaysHours
    .map(parseRange)
    .filter((range): range is [number, number] => range !== null)
  const hasOpeningPeriod = todaysRanges.some(([start, end]) => start !== end)
  const hasClosedSentinel = todaysRanges.some(([start, end]) => start === 0 && end === 0)
  const hasOvernightOpeningPeriod = hasOvernightOpening(openingTimes, date.getDay())

  const isExplicitlyClosed = todaysHours.length === 0 || hasClosedSentinel
  return isExplicitlyClosed && !hasOpeningPeriod && !hasOvernightOpeningPeriod
}

const shiftDateByDays = (date: Date, days: number): Date => {
  const shiftedDate = new Date(date)
  shiftedDate.setDate(shiftedDate.getDate() + days)
  return shiftedDate
}

const calendarDayValue = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).valueOf()

/**
 * Finds an open date without leaving the permitted interval. Forward dates are preferred so an
 * automatic correction never silently shortens a planned rental unless there is no later option.
 */
export const findNearestOpenDate = (
  openingTimes: LocationOpeningTimes[] | undefined,
  preferredDate: Date,
  minDate: Date,
  maxDate: Date
): Date | undefined => {
  const forwardStart =
    calendarDayValue(preferredDate) < calendarDayValue(minDate) ? minDate : preferredDate
  for (
    let candidate = new Date(forwardStart);
    calendarDayValue(candidate) <= calendarDayValue(maxDate);
    candidate = shiftDateByDays(candidate, 1)
  ) {
    if (!isClosedOnDate(openingTimes, candidate)) {
      return candidate
    }
  }

  const backwardStart =
    calendarDayValue(preferredDate) > calendarDayValue(maxDate)
      ? maxDate
      : shiftDateByDays(preferredDate, -1)
  for (
    let candidate = new Date(backwardStart);
    calendarDayValue(candidate) >= calendarDayValue(minDate);
    candidate = shiftDateByDays(candidate, -1)
  ) {
    if (!isClosedOnDate(openingTimes, candidate)) {
      return candidate
    }
  }

  return undefined
}

const hasAfterHoursDropOff = (
  location: CarHireLocation | undefined | null,
  brandId: string
): boolean => {
  const indicator =
    findVendor(location, brandId)?.afterHoursIndicator ?? location?.afterHoursIndicator
  return indicator === true || indicator === 'true'
}

/**
 * The opening hours to constrain a time picker with, given the brands that can serve the trip.
 *
 * When both brands can serve it, the location's own hours already are the union of the two - the
 * trip is bookable while either desk is open. When only one brand can serve it, that brand's own
 * hours are the real constraint, and showing the union would let a customer pick a time that only
 * the other, unusable brand is open for.
 */
export const getServingOpeningTimes = (
  location: CarHireLocation | undefined | null,
  servingBrandIds: string[],
  allowAfterHoursDropOff = false
): LocationOpeningTimes[] | undefined => {
  if (!location) {
    return undefined
  }
  const hasAfterHoursReturn = servingBrandIds.length
    ? servingBrandIds.some((brandId) => hasAfterHoursDropOff(location, brandId))
    : hasAfterHoursDropOff(location, '')
  if (allowAfterHoursDropOff && hasAfterHoursReturn) {
    return undefined
  }
  if (servingBrandIds.length !== 1) {
    return location.openingTimes
  }
  return findVendor(location, servingBrandIds[0])?.openingTimes ?? location.openingTimes
}

export type QualifiedBrandParams = {
  pickUpLocation?: CarHireLocation | null
  dropOffLocation?: CarHireLocation | null
  pickUpDateTime: Date
  dropOffDateTime: Date
}

/**
 * The brands that can actually serve this trip: shared by both ends, open when the car is collected,
 * and either open when it comes back or offering an after-hours key drop.
 *
 * Sending only these means we never ask upstream for a brand whose desk is shut - which upstream
 * answers with a raw 500 rather than an empty result - and a one-way trip fans out to two calls
 * only when two brands can genuinely serve it.
 */
export const getQualifiedBrandIds = ({
  pickUpLocation,
  dropOffLocation,
  pickUpDateTime,
  dropOffDateTime,
}: QualifiedBrandParams): string[] => {
  const returnLocation = dropOffLocation ?? pickUpLocation

  return getCommonBrandIds(pickUpLocation, dropOffLocation).filter((brandId) => {
    if (!isBrandOpenAt(pickUpLocation, brandId, pickUpDateTime)) {
      return false
    }
    return (
      isBrandOpenAt(returnLocation, brandId, dropOffDateTime) ||
      hasAfterHoursDropOff(returnLocation, brandId)
    )
  })
}

/**
 * True only when we can show that no brand can serve the trip at the chosen times, so the customer
 * is told before a search that cannot return anything is sent.
 */
export const hasNoBrandOpen = (params: QualifiedBrandParams): boolean =>
  getLocationBrandIds(params.pickUpLocation).length > 0 && getQualifiedBrandIds(params).length === 0

export type VendorDeskRequest = {
  brandId: string
  afterHoursIndicator?: boolean
  openingTimes?: { day: string; hours: string[] }[]
}

const dayNameFor = (date: Date, offset: number): string =>
  WEEK_DAYS[(((date.getDay() + offset) % 7) + 7) % 7].toUpperCase()

/** Display formatting turned "05:00-02:00" into "05:00 — 02:00"; the API wants it back plain. */
const toApiPeriod = (period: string): string =>
  period
    .split(TIME_RANGE_SEPARATOR)
    .map((part) => part.trim())
    .join('-')

/**
 * The location's per-brand desks in the availability request's shape, so the service can re-run
 * the opening-hours filter itself for callers that skipped this UI (stale URLs, old forms).
 *
 * Trimmed to the days the trip touches (each date's own day plus the previous one, for periods
 * that cross midnight) - the service judges exactly those moments, and the rest would only bloat
 * the results-page URL the desks travel in.
 */
export const getVendorDesksForRequest = (
  location: CarHireLocation | undefined | null,
  dates: Date[]
): VendorDeskRequest[] | undefined => {
  if (!location?.vendors?.length) {
    return undefined
  }
  const relevantDays = new Set(dates.flatMap((date) => [dayNameFor(date, 0), dayNameFor(date, -1)]))

  return location.vendors
    .filter((vendor): vendor is CarHireLocationVendor & { brandId: string } => !!vendor.brandId)
    .map((vendor) => {
      const openingTimes = (vendor.openingTimes ?? [])
        .filter((entry) => relevantDays.has(entry.day.trim().toUpperCase()))
        .map((entry) => ({
          day: entry.day.trim().toUpperCase(),
          hours: entry.hours.map(toApiPeriod),
        }))
      const afterHours = vendor.afterHoursIndicator ?? location.afterHoursIndicator
      return {
        brandId: vendor.brandId,
        ...(afterHours === true || afterHours === 'true' ? { afterHoursIndicator: true } : {}),
        ...(openingTimes.length ? { openingTimes } : {}),
      }
    })
}
